import {
  DYNAMIC_MODAL_VIEWS,
  SELECTED_MENU_ITEMS,
} from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PhotoDrawerSortingOptions } from '../DrawerPhotoDrawer.types';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import braze from 'src/_shared/braze';
import { pickImages } from 'src/store/_shared/imagePickerUtils';
import {
  SourcesEventType,
  SelectedMenuItem2SourcesEventTypeMap,
} from '../../modals/dynamic-modals/modal-upload-multisource/ModalUploadMultisource.constants';
import { ThumbnailImageModelInstance } from 'src/store/_models/thumbnailImage';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';
import { useStore } from 'src/contexts/StoreContext';
import { useMultiSelection } from 'src/hooks/use-multi-selection/useMultiSelection';
import { useMultiSelectMouse } from 'src/editor-v3/hooks/useMultiSelectMouse';
import { View } from 'react-native';

export const useDrawerPhotoPickerLogic = () => {
  const { i18n, drawer, dynamicModal, project, toastStore, photoDrawer } = useStore();
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number>(0);

  const PhotoDrawerSortingOptionsLabels = useMemo(() => {
    return {
      [PhotoDrawerSortingOptions.SHOT_MOST_RECENT]: i18n.t('photo-drawer:shot-date-most-recent'),
      [PhotoDrawerSortingOptions.SHOT_LESS_RECENT]: i18n.t('photo-drawer:shot-date-less-recent'),
      [PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT]: i18n.t('photo-drawer:upload-date-most-recent'),
      [PhotoDrawerSortingOptions.UPLOAD_LESS_RECENT]: i18n.t('photo-drawer:upload-date-less-recent'),
      [PhotoDrawerSortingOptions.NAME_ALPHABETIC]: i18n.t('photo-drawer:name-alphabetic'),
      [PhotoDrawerSortingOptions.NAME_ALPHABETIC_REVERSE]: i18n.t('photo-drawer:name-alphabetic-reverse'),
    };
  }, [i18n]);

  const sendAmplitudeEvents = (option: string) => {
    amplitude.trackEvent(AnalyticsEvents.personalize.photoDrawerOrderBy, {
      order_by: option,
    });
  };

  const [dropdownSelectedOption, setDropDownSelectedOption] = useState<PhotoDrawerSortingOptions>(
    PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT,
  );

  const dropdownOptions = useMemo(() => {
    return [
      PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.SHOT_MOST_RECENT],
      PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.SHOT_LESS_RECENT],
      PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT],
      PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.UPLOAD_LESS_RECENT],
      PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.NAME_ALPHABETIC],
      PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.NAME_ALPHABETIC_REVERSE],
    ];
  }, [PhotoDrawerSortingOptionsLabels]);

  const onPressSourceButton = async (source: SELECTED_MENU_ITEMS) => {
    const sourceType = SelectedMenuItem2SourcesEventTypeMap[source];
    photoDrawer.setPicker(source);
    amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceClick, {
      source_type: sourceType,
      section_name: SECTION_NAMES.DRAWER,
    });

    if (source === SELECTED_MENU_ITEMS.Computer) {
      const productImages: PickedImage[] = await pickImages({
        allowsMultipleSelection: true,
        i18n,
        errorCallback: ({ errorMessage }) => {
          dynamicModal.closeModal();
          toastStore.showErrorToast({
            id: 'alert-uploading-photos-error-toast',
            message: errorMessage,
          });
        },
        newRoute: true,
      });
      if (!!productImages.length) {
        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick, {
          source_type: SourcesEventType.Computer,
          section_name: SECTION_NAMES.DRAWER,
        });
        braze.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick);

        //const autoselect = activeProject!.thumbnailImages.length == 0;
        productImages.forEach(productImage => (productImage.source = SELECTED_MENU_ITEMS.Computer));
        await project.activeProject?.addImages(productImages);

        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
          n_photo: productImages.length,
          source_type: SourcesEventType.Computer,
          section_name: SECTION_NAMES.DRAWER,
        });
      }
    }
  };

  const onPressBack = async () => {
    photoDrawer.setPicker(SELECTED_MENU_ITEMS.Computer);
  };

  const onDragToGrid = (node: any, image: ThumbnailImageModelInstance) => {
    if (node && !project?.isUploading) {
      node.draggable = true;
      node.ondragstart = (e: any) => {
        const selectedThumbnails = project.activeProject?.selectedThumbnails ?? [];
        const isCurrentSelected = selectedThumbnails.some(t => t.originalImageId === image.originalImageId);
        const thumbnailsToDrag = isCurrentSelected && selectedThumbnails.length > 0 ? [...selectedThumbnails] : [image];
        const ids = thumbnailsToDrag.map(t => t.originalImageId);
        e.dataTransfer?.setData('application/x-drawer-photos', JSON.stringify(ids));

        const OFFSET = 4;
        const BADGE = 20;
        const count = thumbnailsToDrag.length;
        const MAX_VISIBLE = Math.min(count, 4);
        const span = (MAX_VISIBLE - 1) * OFFSET;

        const ITEM_HEIGHT = 170;
        const dims = thumbnailsToDrag.slice(0, MAX_VISIBLE).map(thumb => {
          const h = ITEM_HEIGHT;
          const w = thumb.height > 0 ? Math.round((thumb.width / thumb.height) * h) : h;
          return { w, h, url: thumb.thumbnailUrl };
        });

        const maxW = Math.max(...dims.map(d => d.w));
        const maxH = Math.max(...dims.map(d => d.h));

        const ghost = document.createElement('div');
        ghost.style.cssText = `position:fixed;top:-9999px;left:-9999px;width:${maxW + span + BADGE}px;height:${maxH + span + BADGE}px;pointer-events:none;`;

        for (let i = MAX_VISIBLE - 1; i >= 0; i--) {
          const img = document.createElement('img');
          img.src = dims[i].url;
          const pos = (MAX_VISIBLE - 1 - i) * OFFSET + BADGE / 2;
          const rot = i === 0 ? 0 : i % 2 === 0 ? 6 : -6;
          const cx = pos + (maxW - dims[i].w) / 2;
          const cy = pos + (maxH - dims[i].h) / 2;
          img.style.cssText = `width:${dims[i].w}px;height:${dims[i].h}px;object-fit:cover;border-radius:4px;position:absolute;left:${cx}px;top:${cy}px;transform:rotate(${rot}deg);box-shadow:0 2px 8px rgba(0,0,0,0.3);`;
          ghost.appendChild(img);
        }

        if (isCurrentSelected && count > 1) {
          const badge = document.createElement('div');
          badge.textContent = String(count);
          badge.style.cssText = `position:absolute;top:0;right:0;background:white;color:#00A2AB;border-radius:50%;width:${BADGE}px;height:${BADGE}px;line-height:${BADGE}px;text-align:center;font-size:14px;font-weight:700;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);`;
          ghost.appendChild(badge);
        }
        document.body.appendChild(ghost);
        const frontPos = span + BADGE / 2;
        e.dataTransfer?.setDragImage(ghost, frontPos + dims[0].w / 2, frontPos + dims[0].h / 2);
        setTimeout(() => ghost.parentNode?.removeChild(ghost), 0);
      };
    }
  };

  const { allowMultiSelect } = useMultiSelection([drawer.drawerVisible]);
  const layoutMap = useRef<Record<number, { x: number; y: number; width: number; height: number }>>({});
  const scrollOffsetRef = useRef(0);
  const wrapperRef = useRef<View>(null);

  useEffect(() => {
    wrapperRef.current?.measure((fx, fy, width, height, px, py) => {
      wrapperRef.current!.y = py; // store absolute Y offset
    });

    project.setShowNotificationPhotoDrawer(false);
    amplitude.trackEvent(AnalyticsEvents.personalize.yourPhotoOpen, {
      source: drawer.openPoint,
      context: project.activeGroup ? 'products_view' : 'groups_view',
    });
  }, []);

  useEffect(() => {
    if (project.activeProject?.hasShareToken) {
      project.stopShareObserver();
      project.startShareObserver();
    }

    return () => {
      project.stopShareObserver();
    };
  }, [project.activeProject?.hasShareToken]);

  const images = project.activeProject?.thumbnailImages;

  const sortedImages = useMemo(() => {
    const imageSlice = images?.slice();

    return imageSlice?.sort((a, b) => {
      if (dropdownSelectedOption === PhotoDrawerSortingOptions.SHOT_MOST_RECENT) {
        return b.fileLastModified - a.fileLastModified;
      } else if (dropdownSelectedOption === PhotoDrawerSortingOptions.SHOT_LESS_RECENT) {
        return a.fileLastModified - b.fileLastModified;
      } else if (dropdownSelectedOption === PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT) {
        return b.fileAddedTimestamp - a.fileAddedTimestamp;
      } else if (dropdownSelectedOption === PhotoDrawerSortingOptions.UPLOAD_LESS_RECENT) {
        return a.fileAddedTimestamp - b.fileAddedTimestamp;
      } else if (dropdownSelectedOption === PhotoDrawerSortingOptions.NAME_ALPHABETIC) {
        return a.fileName.localeCompare(b.fileName, undefined, { numeric: true });
      } else if (dropdownSelectedOption === PhotoDrawerSortingOptions.NAME_ALPHABETIC_REVERSE) {
        return b.fileName.localeCompare(a.fileName, undefined, { numeric: true });
      }
      return b.usageQuantity - a.usageQuantity;
    });
  }, [images, images?.length, dropdownSelectedOption]);

  const onPressCard = (image: ThumbnailImageModelInstance, index: number) => {
    if (allowMultiSelect) {
      const from = Math.min(lastSelectedIndex, index);
      const to = Math.max(lastSelectedIndex, index);
      sortedImages?.forEach((thImage, thIndex) => {
        if (thIndex >= from && thIndex <= to && !thImage?.isSelected) {
          thImage.select();
        }
      });
    } else {
      if (image.isSelected) {
        image.deselect();
      } else {
        setLastSelectedIndex(index);
        image.select();
      }
    }
  };

  const selectedThumbnails = project.activeProject?.selectedThumbnails?.length || 0;
  const ctaLabel =
    selectedThumbnails > 0
      ? i18n.t('modal-picker-my-photos:deselect-all')
      : i18n.t('modal-picker-my-photos:select-all');

  const onPressDelete = () => {
    if (!!project.activeProject?.selectedThumbnails?.length) {
      dynamicModal.openModal(
        {
          dynamicModalView: DYNAMIC_MODAL_VIEWS.DeleteAll,
          thumbnailsToDelete: project.activeProject?.selectedThumbnails?.map(item => item.originalImageId),
        },
        true,
      );
    }
  };

  const isActiveDelete =
    !!project.activeProject?.selectedThumbnails?.length &&
    project.activeProject?.selectedThumbnails?.every(item => item.usageQuantity === 0);

  const computeSelection = () => {
    if (!selection) return;
    project.activeProject?.deselectAllThumbnailImages();
    const left = Math.min(selection.startX, selection.endX);
    const right = Math.max(selection.startX, selection.endX);
    const contentOffsetY = scrollOffsetRef.current;
    const wrapperOffsetY = wrapperRef.current?.y || 0;
    const top = Math.min(selection.startY, selection.endY) + contentOffsetY - wrapperOffsetY;
    const bottom = Math.max(selection.startY, selection.endY) + contentOffsetY - wrapperOffsetY;
    sortedImages?.forEach((image, index) => {
      const layout = layoutMap.current[index];
      if (!layout) return;
      const itemLeft = layout.x;
      const itemRight = layout.x + layout.width;
      const itemTop = layout.y;
      const itemBottom = layout.y + layout.height;
      const overlapX = Math.min(itemRight, right) - Math.max(itemLeft, left);
      const overlapY = Math.min(itemBottom, bottom) - Math.max(itemTop, top);
      if (overlapX <= 0 || overlapY <= 0) return;
      const area = overlapX * overlapY;
      const itemArea = layout.width * layout.height;
      if (area / itemArea > 0.2) {
        image.select();
      }
    });
  };
  const { handleMouseDown, handleMouseMove, selection } = useMultiSelectMouse(computeSelection);

  return {
    dropdownOptions,
    sendAmplitudeEvents,
    dropdownSelectedOption,
    PhotoDrawerSortingOptionsLabels,
    setDropDownSelectedOption,
    onPressSourceButton,
    onPressBack,
    selectedThumbnails,
    ctaLabel,
    isActiveDelete,
    onPressDelete,
    scrollOffsetRef,
    sortedImages,
    wrapperRef,
    handleMouseDown,
    handleMouseMove,
    selection,
    layoutMap,
    onDragToGrid,
    onPressCard,
    images,
  };
};
